/**
 * Firebase AI Logic Test Page
 *
 * Simple test page to verify Firebase AI SDK integration.
 * Access at /ai-test
 */

import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useFirebaseAI, AI_MODELS } from "@/hooks/use-firebase-ai";
import { Loader2, Sparkles, CheckCircle2, XCircle } from "lucide-react";

export default function AITestPage() {
  const [prompt, setPrompt] = useState(
    "Explain Swiss corporate VAT in 2-3 sentences."
  );
  const [configStatus, setConfigStatus] = useState<{
    checked: boolean;
    configured: boolean;
    location: string;
    error?: string;
  } | null>(null);

  const { generate, isLoading, error, lastResponse, checkConfig, reset } =
    useFirebaseAI({
      model: AI_MODELS.GEMINI_2_5_FLASH,
    });

  const handleCheckConfig = async () => {
    const result = await checkConfig();
    setConfigStatus({ checked: true, ...result });
  };

  const handleGenerate = async () => {
    try {
      await generate(prompt);
    } catch {
      // Error is handled by the hook
    }
  };

  return (
    <DashboardLayout>
      <div className="container mx-auto p-6 max-w-4xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Firebase AI Logic Test</h1>
          <p className="text-muted-foreground">
            Test the Firebase AI SDK integration with Vertex AI in Zurich
            (europe-west6).
          </p>
        </div>

        {/* Configuration Check */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              Configuration Status
            </CardTitle>
            <CardDescription>
              Verify Firebase AI is properly configured for Swiss data
              residency.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {configStatus === null ? (
              <Button onClick={handleCheckConfig} variant="outline">
                Check Configuration
              </Button>
            ) : configStatus.configured ? (
              <div className="flex items-center gap-2 text-success">
                <CheckCircle2 className="h-5 w-5" />
                <span>
                  AI configured successfully! Using Vertex AI in{" "}
                  <strong>{configStatus.location}</strong> (Zurich, Switzerland)
                </span>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-destructive">
                  <XCircle className="h-5 w-5" />
                  <span>Configuration issue detected</span>
                </div>
                {configStatus.error && (
                  <pre className="text-sm bg-muted p-3 rounded-md overflow-x-auto">
                    {configStatus.error}
                  </pre>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Test Generation */}
        <Card>
          <CardHeader>
            <CardTitle>Test AI Generation</CardTitle>
            <CardDescription>
              Send a prompt to Gemini 2.0 Flash via Firebase AI SDK.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Prompt</label>
              <Textarea
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                placeholder="Enter your prompt..."
                rows={3}
              />
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleGenerate}
                disabled={isLoading || !prompt.trim()}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Generate
                  </>
                )}
              </Button>
              {(lastResponse || error) && (
                <Button variant="outline" onClick={reset}>
                  Clear
                </Button>
              )}
            </div>

            {error && (
              <div className="p-4 bg-destructive/5 border border-destructive/20 rounded-lg">
                <p className="text-sm text-destructive font-medium">Error</p>
                <p className="text-sm text-destructive mt-1">{error}</p>
              </div>
            )}

            {lastResponse && (
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm font-medium mb-2">Response</p>
                <p className="text-sm whitespace-pre-wrap">{lastResponse}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Technical Details */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Technical Details</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <dt className="font-medium text-muted-foreground">SDK</dt>
                <dd>@firebase/ai v2.6.1</dd>
              </div>
              <div>
                <dt className="font-medium text-muted-foreground">Backend</dt>
                <dd>Vertex AI (VertexAIBackend)</dd>
              </div>
              <div>
                <dt className="font-medium text-muted-foreground">Region</dt>
                <dd>europe-west6 (Zurich)</dd>
              </div>
              <div>
                <dt className="font-medium text-muted-foreground">Model</dt>
                <dd>gemini-2.5-flash</dd>
              </div>
            </dl>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
